<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			:search-query.sync="systemEmailSearchQuery"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			search-query-label="System Email Name"
			@persistSearchQuery="changeSystemEmailSearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
			@clearFilters="clearFilters"
		>
			<template #after-filters>
				<Dropdown
					:value="systemEmailTypeFilter"
					label="Email Type"
					:items="emailTypeItems"
					custom-sort
					@input="changeSystemEmailTypeFilter($event)"
				/>
				<Dropdown
					v-model="systemEmailTranslationStatusId"
					:items="showTranslatedOnlyOptions"
					custom-sort
					label="Translation Status"
					@input="changeSystemEmailTranslationStatusId($event)"
				/>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				System Email Translations
			</template>
			<template #title-action>
				<Dropdown
					:value="selectedLanguageIdOrDefault"
					label="Language"
					searchable
					:items="assignedLanguagesItems"
					@input="selectedLanguageId = $event"
				/>
			</template>
			<template #body>
				<DataTable
					:headers="tableHeaders"
					:items="filteredSystemEmailTranslations"
					sort-by="systemEmailId"
					sort-desc
					@click:row="onEditSystemEmailTranslation"
				>
					<template #item.isPublicPortalConfirmationEmail="{ item }">
						{{ item.isPublicPortalConfirmationEmail ? 'Yes' : 'No' }}
					</template>
					<template #item.action="{ item }">
						<v-menu offset-y>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									small
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId)"
									@click="onEditSystemEmailTranslation(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Edit
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-else
									@click="onEditSystemEmailTranslation(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											View
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId) && item.translated == 'Translated'"
									@click="openSendTestEmailModal(item)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Send Test Email
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId) && item.translated == 'Translated'"
									@click="systemEmailTranslationToRemove = item"
								>
									<v-list-item-content>
										<v-list-item-title>
											Delete
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<ConfirmDeleteModal
			v-if="systemEmailTranslationToRemove"
			:entity-name="`${systemEmailTranslationToRemove.systemEmailName} Translation`"
			entity-type="System Email Translation"
			@close="systemEmailTranslationToRemove = null"
			@delete="deleteSystemEmailTranslation"
		/>
		<SendTestEmailModal
			v-if="systemEmailTestSendModal"
			:system-email-item="systemEmailItem"
			@close="systemEmailTestSendModal = null"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import SectionCard from '../../../../../shared/components/section-card.vue'
import FilterBar from '../../../../../shared/components/filter-bar.vue'
import DataTable from '../../../../../shared/components/data-table.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import ConfirmDeleteModal from '../../../../../shared/components/confirm-delete-modal.vue'
import SendTestEmailModal from './confirm-email-test-send-modal.vue'
import { getAssignedLanguages } from '../../../../../shared/utils/api/languages.js'
import { MANAGE_SYSTEM_EMAIL_TRANSLATION } from '../../../router/route-names.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { SYSTEM_EMAILS_MODULE_FULL_PERMISSIONS } from '../../../../../shared/permissions/admin-portal-permissions.js'
import { useLabels } from '../../../../../shared/state/admin-portal-navigation.js'
import { deleteSystemEmailTranslation } from '../../../../../shared/utils/api/system-emails.js'
import { changeSystemEmailSearchQuery, systemEmailSearchQuery, changeSystemEmailTypeFilter, systemEmailTypeFilter, changeSystemEmailTranslationStatusId, systemEmailTranslationStatusId } from '../../../../../shared/state/system-emails.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId, userHasBrand } from '../../../../../shared/state/brands.js'

export default {
	components: { DataTable, FilterBar, SectionCard, Dropdown, ConfirmDeleteModal, SendTestEmailModal },
	props: {
		systemEmails: {
			type: Array,
			default: () => []
		},
		emailTypes: {
			type: Array,
			default: () => []
		},
		emailTypeItems: {
			type: Array,
			default: () => []
		},
		previouslySelectedLanguage: Number
	},
	setup () {
		return {
			userHasBrand,
			showSnackbar,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			changeSystemEmailSearchQuery,
			systemEmailSearchQuery,
			changeSystemEmailTypeFilter,
			systemEmailTypeFilter,
			changeSystemEmailTranslationStatusId,
			systemEmailTranslationStatusId
		}
	},
	data () {
		return {
			systemEmailTranslationToRemove: null,
			assignedLanguages: [],
			selectedLanguageId: null,
			systemEmailTestSendModal: false,
			systemEmailItem: false
		}
	},
	computed: {
		...mapGetters({
			checkPermission: 'auth/productAreaPermission'
		}),
		systemEmailTranslations () {
			return this.systemEmails
				.map(email => {
					const systemEmailTranslation = email.systemEmailTranslations.find(({ languageID }) => languageID === this.selectedLanguageIdOrDefault)
					const language = this.assignedLanguagesItems.find(({ value }) => value === this.selectedLanguageIdOrDefault)
					const emailType = this.emailTypes?.find(({ emailTypeId }) => emailTypeId === email.emailTypeId)
					return {
						...email,
						translatedEmailSubject: systemEmailTranslation?.translatedEmailSubject,
						translatedEmailHTML: systemEmailTranslation?.translatedEmailHTML,
						systemEmailTranslationID: systemEmailTranslation?.systemEmailTranslationID,
						languageName: language?.text,
						languageId: language?.value,
						translated: systemEmailTranslation?.translatedEmailSubject || systemEmailTranslation?.translatedEmailHTML ? 'Translated' : 'Untranslated',
						emailTypeName: emailType?.emailTypeDescription ?? ''
					}
				})
		},
		selectedLanguageIdOrDefault () {
			if (this.selectedLanguageId !== null) return this.selectedLanguageId
			if (this.previouslySelectedLanguage != null) return this.previouslySelectedLanguage
			return this.assignedLanguagesItems[0]?.value
		},
		assignedLanguagesItems () {
			const DEFAULT_LANGUAGE = 0
			return this.assignedLanguages.map(({ languageID, languageName }) => ({
				value: languageID,
				text: languageName
			})).filter(({ value }) => value !== DEFAULT_LANGUAGE)
		},
		filteredSystemEmailTranslations () {
			return this.systemEmailTranslations.filter(({ systemEmailName, brandId, emailTypeId, translated }) => {
				let check = true
				if (systemEmailSearchQuery.value) check = systemEmailName.toLowerCase().includes(systemEmailSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && selectedAdminPortalBrandId.value === brandId
				if (systemEmailTypeFilter.value !== null) check = check && systemEmailTypeFilter.value === emailTypeId
				if (systemEmailTranslationStatusId.value !== null) check = check && systemEmailTranslationStatusId.value === translated
				return check
			})
		},
		userFullPermissions () {
			return this.checkPermission(SYSTEM_EMAILS_MODULE_FULL_PERMISSIONS)
		},
		tableHeaders () {
			return [
				{ value: 'systemEmailId', text: 'ID', width: '6%' },
				{ value: 'brandName', text: useLabels('Brand'), width: '12%' },
				{ value: 'systemEmailName', text: 'System Email Name', width: '200px' },
				{ value: 'emailTypeName', text: 'Email Type', width: '12%' },
				{ value: 'isPublicPortalConfirmationEmail', text: 'Public Portal Confirmation Email?', width: '22%' },
				{ value: 'translated', text: 'Status', width: '10%' },
				{ value: 'createdByDate', text: 'Created Date', width: '18%' },
				{ value: 'action', text: 'Action', width: '80px', align: 'center', sortable: false }
			]
		},
		showTranslatedOnlyOptions () {
			return [
				{ value: null, text: 'View All' },
				{ value: 'Translated', text: 'Translated Only' },
				{ value: 'Untranslated', text: 'Untranslated Only' }
			]
		}
	},
	created () {
		this.loadLanguages()
	},
	methods: {
		async loadLanguages () {
			const { data: assignedLanguages } = await getAssignedLanguages()
			this.assignedLanguages = assignedLanguages
		},
		onEditSystemEmailTranslation (systemEmailTranslation) {
			this.$router.push({
				name: MANAGE_SYSTEM_EMAIL_TRANSLATION,
				params: {
					systemEmailToTranslate: systemEmailTranslation
				}
			})
		},
		async deleteSystemEmailTranslation () {
			await deleteSystemEmailTranslation(this.systemEmailTranslationToRemove.systemEmailTranslationID)
			showSnackbar('System Email Translation deleted successfully')
			this.$emit('load-system-emails')
			this.systemEmailTranslationToRemove = null
		},
		clearFilters () {
			changeSystemEmailTypeFilter(null)
			changeSystemEmailTranslationStatusId(null)
		},
		openSendTestEmailModal (systemEmailItem) {
			this.systemEmailItem = systemEmailItem
			this.systemEmailTestSendModal = true
		}
	}
}
</script>
